import React, { useEffect, useMemo, useState } from "react";
import { Plus, Save, Trash2, X, Copy } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const CATEGORIES = ["voli", "guide", "permessi", "alloggi", "trasporti", "pasti", "assicurazione", "equipaggiamento", "altro"];

const EMPTY = {
  name: "", destination: "", pax: 8, min_pax: 4, duration_days: 7, currency: "EUR",
  markup_percent: 25, notes: "", cost_items: [{ label: "", category: "voli", cost_type: "per_person", amount: 0 }],
};

function compute(p) {
  const items = p.cost_items || [];
  const per = items.filter((i) => i.cost_type === "per_person").reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const fixed = items.filter((i) => i.cost_type === "fixed").reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const pax = Math.max(parseInt(p.pax) || 1, 1);
  const costPP = per + fixed / pax;
  const selling = costPP * (1 + (parseFloat(p.markup_percent) || 0) / 100);
  const marginPP = selling - costPP;
  return {
    perPerson: per,
    fixedTotal: fixed,
    costPerPerson: costPP,
    sellingPrice: selling,
    marginPerPerson: marginPP,
    marginPercent: selling ? (marginPP / selling) * 100 : 0,
    totalCost: per * pax + fixed,
    totalRevenue: selling * pax,
    profit: selling * pax - (per * pax + fixed),
    breakeven: selling > per ? Math.ceil(fixed / (selling - per)) : null,
  };
}

const eur = (n) => `€${Math.round(n || 0).toLocaleString("it-IT")}`;

export default function AdminPlanning() {
  const [plans, setPlans] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/plans/").then((r) => setPlans(r.data.items)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const isNew = editing && !editing.id;
  const live = editing ? compute(editing) : null;

  const openNew = () => setEditing({ ...EMPTY, cost_items: [{ label: "", category: "voli", cost_type: "per_person", amount: 0 }] });
  const openEdit = (p) => setEditing(JSON.parse(JSON.stringify(p)));

  const setF = (k, v) => setEditing((e) => ({ ...e, [k]: v }));
  const setItem = (i, k, v) => setEditing((e) => ({ ...e, cost_items: e.cost_items.map((c, j) => (j === i ? { ...c, [k]: v } : c)) }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...editing,
        pax: parseInt(editing.pax) || 1,
        min_pax: parseInt(editing.min_pax) || 1,
        duration_days: parseInt(editing.duration_days) || 1,
        markup_percent: parseFloat(editing.markup_percent) || 0,
        cost_items: (editing.cost_items || []).filter((c) => c.label).map((c) => ({ ...c, amount: parseFloat(c.amount) || 0 })),
      };
      delete payload.id;
      delete payload.created_at;
      delete payload.updated_at;
      delete payload.computed;
      if (editing.id) {
        await api.put(`/plans/${editing.id}`, payload);
        toast.success("Piano aggiornato");
      } else {
        await api.post("/plans/", payload);
        toast.success("Piano creato");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Eliminare il piano "${p.name}"?`)) return;
    try {
      await api.delete(`/plans/${p.id}`);
      toast.success("Piano eliminato");
      if (editing && editing.id === p.id) setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const bestMargin = useMemo(() => {
    if (!plans.length) return null;
    return plans.reduce((best, p) => (p.computed.profit > (best ? best.computed.profit : -Infinity) ? p : best), null);
  }, [plans]);

  return (
    <div data-testid="admin-planning">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl">Tour Planning & Markup</h1>
          <p className="text-cream2 text-sm mt-1">Progetta il costo delle spedizioni e imposta il prezzo di vendita.</p>
        </div>
        <button onClick={openNew} className="btn-copper !px-5 !py-2.5" data-testid="planning-add-btn"><Plus className="h-4 w-4" /> Nuovo piano</button>
      </div>

      {/* COMPARISON TABLE */}
      <div className="card-dark mt-8 overflow-x-auto" data-testid="cost-comparison-table">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="border-b border-white/5 text-left font-mono text-[10px] uppercase tracking-[0.18em] text-sand">
              <th className="px-5 py-4">Piano</th>
              <th className="px-5 py-4">Durata</th>
              <th className="px-5 py-4">Pax</th>
              <th className="px-5 py-4">Costo/persona</th>
              <th className="px-5 py-4">Di cui fissi</th>
              <th className="px-5 py-4">Markup</th>
              <th className="px-5 py-4">Prezzo vendita</th>
              <th className="px-5 py-4">Margine/pers.</th>
              <th className="px-5 py-4">Profitto</th>
              <th className="px-5 py-4">Break-even</th>
              <th className="px-5 py-4" />
            </tr>
          </thead>
          <tbody>
            {plans.map((p) => {
              const c = p.computed;
              const isBest = bestMargin && bestMargin.id === p.id;
              return (
                <tr key={p.id} className={"border-b border-white/5 last:border-0 " + (isBest ? "bg-copper/5" : "hover:bg-white/[0.02]")} data-testid={`planning-row-${p.id}`}>
                  <td className="px-5 py-4">
                    <p className="text-cream font-medium">{p.name}</p>
                    <p className="text-sand text-xs">{p.destination}</p>
                  </td>
                  <td className="px-5 py-4 text-cream2">{p.duration_days} gg</td>
                  <td className="px-5 py-4 text-cream2">{p.pax} (min {p.min_pax})</td>
                  <td className="px-5 py-4 text-cream">{eur(c.cost_per_person)}</td>
                  <td className="px-5 py-4 text-cream2">{eur(c.fixed_total)}</td>
                  <td className="px-5 py-4 text-copperlight">{p.markup_percent}%</td>
                  <td className="px-5 py-4 text-copper font-medium" data-testid={`planning-price-${p.id}`}>{eur(c.selling_price)}</td>
                  <td className="px-5 py-4 text-cream">{eur(c.margin_per_person)} <span className="text-sand text-xs">({c.margin_percent}%)</span></td>
                  <td className={"px-5 py-4 " + (c.profit >= 0 ? "text-green-400" : "text-red-400")}>{eur(c.profit)}</td>
                  <td className="px-5 py-4 text-cream2">{c.breakeven_pax ?? "—"} pax</td>
                  <td className="px-5 py-4">
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => openEdit(p)} className="p-2 text-cream2 hover:text-copper" title="Apri" data-testid={`planning-edit-${p.id}`}><Copy className="h-4 w-4" /></button>
                      <button onClick={() => remove(p)} className="p-2 text-cream2 hover:text-red-400" title="Elimina" data-testid={`planning-delete-${p.id}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {plans.length === 0 && (
              <tr><td colSpan="11" className="px-5 py-10 text-center text-sand">Nessun piano: creane uno per iniziare.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-[90] bg-obsidian/90 backdrop-blur-sm overflow-y-auto p-4 sm:p-8" data-testid="planning-editor-modal">
          <div className="max-w-4xl mx-auto card-dark p-6 sm:p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl">{isNew ? "Nuovo piano di costo" : `Piano · ${editing.name}`}</h2>
              <button onClick={() => setEditing(null)} className="text-sand hover:text-cream" data-testid="planning-editor-close"><X className="h-5 w-5" /></button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div><label className="label">Nome piano *</label><input className="field" value={editing.name} onChange={(e) => setF("name", e.target.value)} data-testid="planning-field-name" /></div>
              <div><label className="label">Destinazione</label><input className="field" value={editing.destination} onChange={(e) => setF("destination", e.target.value)} data-testid="planning-field-destination" /></div>
              <div><label className="label">Durata (giorni)</label><input type="number" className="field" value={editing.duration_days} onChange={(e) => setF("duration_days", e.target.value)} data-testid="planning-field-days" /></div>
              <div><label className="label">Pax (dimensione gruppo)</label><input type="number" className="field" value={editing.pax} onChange={(e) => setF("pax", e.target.value)} data-testid="planning-field-pax" /></div>
              <div><label className="label">Pax minimo</label><input type="number" className="field" value={editing.min_pax} onChange={(e) => setF("min_pax", e.target.value)} data-testid="planning-field-minpax" /></div>
              <div>
                <label className="label">Markup (%) — <span className="text-copper" data-testid="planning-markup-value">{editing.markup_percent}%</span></label>
                <input type="range" min="0" max="100" step="1" className="w-full accent-[#C86D3B]" value={editing.markup_percent} onChange={(e) => setF("markup_percent", e.target.value)} data-testid="markup-slider-input" />
              </div>
            </div>

            <div className="mt-6 flex items-center justify-between">
              <label className="label !mb-0">Voci di costo</label>
              <button
                className="font-mono text-[10px] uppercase tracking-widest text-copperlight border border-copper/30 px-3 py-1.5"
                onClick={() => setF("cost_items", [...editing.cost_items, { label: "", category: "altro", cost_type: "per_person", amount: 0 }])}
                data-testid="planning-add-cost-row"
              >
                + Voce
              </button>
            </div>
            <div className="mt-3 space-y-2" data-testid="cost-breakdown-table">
              {editing.cost_items.map((c, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center border border-white/5 bg-obsidian2 p-2.5" data-testid={`planning-cost-row-${i + 1}`}>
                  <input className="field !py-1.5 col-span-12 sm:col-span-4" placeholder="Voce (es. Voli interni)" value={c.label} onChange={(e) => setItem(i, "label", e.target.value)} data-testid={`planning-cost-label-${i + 1}`} />
                  <select className="field !py-1.5 col-span-4 sm:col-span-2" value={c.category} onChange={(e) => setItem(i, "category", e.target.value)} data-testid={`planning-cost-category-${i + 1}`}>
                    {CATEGORIES.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
                  </select>
                  <select className="field !py-1.5 col-span-4 sm:col-span-2" value={c.cost_type} onChange={(e) => setItem(i, "cost_type", e.target.value)} data-testid={`planning-cost-type-${i + 1}`}>
                    <option value="per_person">Per persona</option>
                    <option value="fixed">Fisso (intero gruppo)</option>
                  </select>
                  <input type="number" className="field !py-1.5 col-span-3 sm:col-span-3" placeholder="€" value={c.amount} onChange={(e) => setItem(i, "amount", e.target.value)} data-testid={`planning-cost-amount-${i + 1}`} />
                  <button onClick={() => setF("cost_items", editing.cost_items.filter((_, j) => j !== i))} className="col-span-1 text-sand hover:text-red-400 justify-self-center" data-testid={`planning-cost-remove-${i + 1}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>

            <div className="mt-4"><label className="label">Note</label><textarea className="field resize-none" rows="2" value={editing.notes} onChange={(e) => setF("notes", e.target.value)} data-testid="planning-field-notes" /></div>

            {/* LIVE COMPUTED */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mt-8" data-testid="planning-computed">
              <div className="card-dark p-4"><p className="font-mono text-[9px] uppercase tracking-widest text-sand">Costo/persona</p><p className="font-serif text-2xl text-cream mt-1" data-testid="planning-cost-per-person">{eur(live.costPerPerson)}</p></div>
              <div className="card-dark p-4"><p className="font-mono text-[9px] uppercase tracking-widest text-sand">Prezzo vendita</p><p className="font-serif text-2xl text-copper mt-1" data-testid="final-price-output">{eur(live.sellingPrice)}</p></div>
              <div className="card-dark p-4"><p className="font-mono text-[9px] uppercase tracking-widest text-sand">Margine/persona</p><p className="font-serif text-2xl text-cream mt-1" data-testid="margin-per-person-output">{eur(live.marginPerPerson)} <span className="text-xs text-sand">{live.marginPercent}%</span></p></div>
              <div className="card-dark p-4"><p className="font-mono text-[9px] uppercase tracking-widest text-sand">Profitto totale</p><p className={"font-serif text-2xl mt-1 " + (live.profit >= 0 ? "text-green-400" : "text-red-400")} data-testid="margin-profit-card">{eur(live.profit)}</p></div>
              <div className="card-dark p-4"><p className="font-mono text-[9px] uppercase tracking-widest text-sand">Break-even</p><p className="font-serif text-2xl text-cream mt-1" data-testid="breakeven-output">{live.breakeven ?? "—"} pax</p></div>
            </div>

            <div className="flex justify-end gap-3 mt-8">
              <button onClick={() => setEditing(null)} className="btn-ghost !px-5 !py-2.5" data-testid="planning-cancel">Annulla</button>
              <button onClick={save} disabled={busy} className="btn-copper !px-6 !py-2.5 disabled:opacity-50" data-testid="planning-save"><Save className="h-4 w-4" /> {busy ? "Salvo…" : "Salva piano"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
